import React, { useState } from "react";
import AdvisorModal from "./AdvisorModal"; 
import "font-awesome/css/font-awesome.min.css";

export default function PremiumCalculator() {
  // Calculator state
  const [monthlyExpenses, setMonthlyExpenses] = useState(40000);
  const [currentAge, setCurrentAge] = useState(23);
  const [loanAmount, setLoanAmount] = useState(200000);
  const [existingCover, setExistingCover] = useState(0);

  // Results state
  const [totalCoverNeeded, setTotalCoverNeeded] = useState(14037151);
  const [humanLifeValue, setHumanLifeValue] = useState(14237151);
  const [recommendedCover, setRecommendedCover] = useState(20000000);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Coverage calculation
  const calculateCoverage = () => {
    const retirementAge = 60;
    const hlv =
      monthlyExpenses * 12 * (retirementAge - currentAge) +
      loanAmount -
      existingCover;
    const recommended = Math.ceil(hlv / 100000) * 100000; // round to nearest lakh

    setHumanLifeValue(hlv);
    setTotalCoverNeeded(hlv);
    setRecommendedCover(recommended);
  };

  const openAdvisorModal = () => setIsModalOpen(true);
  const closeAdvisorModal = () => setIsModalOpen(false);

  return (
    <section id="premium-calculator" className="lg:tw-py-20 tw-py-10 tw-bg-white">
      <div className="tw-container tw-mx-auto tw-px-6">
        {/* Heading */}
        <div className="tw-text-center tw-mb-12">
          <h2 className="tw-text-2xl lg:tw-text-4xl tw-font-bold tw-text-fintoo-blue tw-mb-6">
            Not sure how much cover you need? Let’s calculate it together.
          </h2>
          <p className="tw-text-lg lg:tw-text-xl tw-text-gray-700">
            Simply enter your details and we’ll recommend the ideal cover
            amount for your family — so you’re neither underinsured nor
            overspending.
          </p>
        </div>

        <div className="tw-max-w-6xl tw-mx-auto">
          <div className="tw-grid md:tw-grid-cols-2 tw-gap-12">
            {/* Calculator Inputs */}
            <div>
              <div className="tw-bg-gradient-to-br tw-from-blue-50 tw-to-orange-50 tw-rounded-3xl tw-p-8">
                <h3 className="tw-text-2xl tw-font-bold tw-text-fintoo-blue tw-mb-6 tw-p-0">
                  Enter Your Details
                </h3>
                <div className="tw-space-y-6">
                  {/* Monthly Expenses */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Total Monthly Expenses
                    </label>
                    <div className="tw-relative">
                      <span className="tw-absolute tw-left-3 tw-top-3 tw-text-gray-500">₹</span>
                      <input
                        type="number"
                        value={monthlyExpenses}
                        onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                        className="tw-w-full tw-pl-8 tw-pr-4 tw-py-3 tw-border tw-border-solid tw-border-gray-300 tw-rounded-xl tw-focus:outline-none tw-focus:tw-ring-2 tw-focus:tw-ring-fintoo-orange"
                      />
                    </div>
                  </div>

                  {/* Current Age */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Current Age
                    </label>
                    <input
                      type="number"
                      value={currentAge}
                      onChange={(e) => setCurrentAge(Number(e.target.value))}
                      className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-solid tw-border-gray-300 tw-rounded-xl tw-focus:outline-none tw-focus:tw-ring-2 tw-focus:tw-ring-fintoo-orange"
                    />
                  </div>

                  {/* Loan Amount */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Outstanding Loan Amount
                    </label>
                    <div className="tw-relative">
                      <span className="tw-absolute tw-left-3 tw-top-3 tw-text-gray-500">₹</span>
                      <input
                        type="number"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(Number(e.target.value))}
                        className="tw-w-full tw-pl-8 tw-pr-4 tw-py-3 tw-border tw-border-solid tw-border-gray-300 tw-rounded-xl tw-focus:outline-none tw-focus:tw-ring-2 tw-focus:tw-ring-fintoo-orange"
                      />
                    </div>
                  </div>

                  {/* Existing Life Cover */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                      Existing Life Cover
                    </label>
                    <div className="tw-relative">
                      <span className="tw-absolute tw-left-3 tw-top-3 tw-text-gray-500">₹</span>
                      <input
                        type="number"
                        value={existingCover}
                        onChange={(e) => setExistingCover(Number(e.target.value))}
                        className="tw-w-full tw-pl-8 tw-pr-4 tw-py-3 tw-border tw-border-solid tw-border-gray-300 tw-rounded-xl tw-focus:outline-none tw-focus:tw-ring-2 tw-focus:tw-ring-fintoo-orange"
                      />
                    </div>
                  </div>

                  <button
                    onClick={calculateCoverage}
                    className="tw-w-full tw-bg-fintoo-orange tw-text-white tw-py-4 tw-rounded-xl tw-font-semibold tw-text-lg tw-transition-colors"
                  >
                    Calculate Coverage Now
                  </button>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="tw-space-y-6">
              <h3 className="tw-text-2xl tw-font-bold tw-text-fintoo-blue tw-p-0">Your Coverage Analysis</h3>

              <div className="tw-bg-gradient-to-b tw-from-fintoo-blue tw-to-[#1b53a3] tw-text-white tw-rounded-3xl tw-p-8 tw-text-center">
                <h4 className="tw-text-lg tw-font-medium tw-mb-2">Total Life Cover Needed</h4>
                <div className="tw-text-4xl tw-font-bold tw-mb-2">₹ {totalCoverNeeded.toLocaleString("en-IN")}</div>
                <p className="tw-text-blue-200">Based on your financial requirements</p>
              </div>

              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <div className="tw-bg-gray-50 tw-rounded-2xl tw-p-6 tw-text-center">
                  <h5 className="tw-text-sm tw-font-medium tw-text-gray-600 tw-mb-2">Existing Life Cover</h5>
                  <div className="tw-text-2xl tw-font-bold tw-text-fintoo-blue">₹ {existingCover.toLocaleString("en-IN")}</div>
                </div>
                <div className="tw-bg-gray-50 tw-rounded-2xl tw-p-6 tw-text-center">
                  <h5 className="tw-text-sm tw-font-medium tw-text-gray-600 tw-mb-2">Human Life Value (HLV)</h5>
                  <div className="tw-text-2xl tw-font-bold tw-text-fintoo-blue">₹ {humanLifeValue.toLocaleString("en-IN")}</div>
                </div>
              </div>

              <div className="tw-bg-gradient-to-r tw-from-orange-50 tw-to-yellow-50 tw-border-2 tw-border-fintoo-orange tw-rounded-2xl tw-p-6">
                <div className="tw-flex tw-items-center tw-mb-3">
                  <i className="fa fa-lightbulb-o tw-text-fintoo-orange tw-text-xl tw-mr-3"></i>
                  <h5 className="tw-text-lg tw-font-semibold tw-text-fintoo-blue">Our Recommendation</h5>
                </div>
                <div className="tw-text-3xl tw-font-bold tw-text-fintoo-orange tw-mb-2">₹ {recommendedCover.toLocaleString("en-IN")}</div>
                <p className="tw-text-gray-700">We recommend rounding up to ensure adequate protection for your family's future needs.</p>
              </div>

              <div className="tw-text-center">
                <button
                  onClick={openAdvisorModal}
                  className="tw-bg-fintoo-blue tw-text-white tw-px-8 tw-py-4 tw-rounded-xl tw-font-semibold tw-text-lg tw-transition-colors"
                >
                  Get Personalized Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && <AdvisorModal onClose={closeAdvisorModal} />}
    </section>
  );
}
